/**
 * Проекты: один список на всё приложение — хаб рисует из него карточки,
 * сайдбар свои пункты. Два независимых запроса за одним и тем же означали бы
 * два разных списка на экране, что читается как ошибка прав.
 *
 * Фильтр и поиск уходят на сервер: список проектов у человека короткий, но
 * фильтровать его на клиенте значит показывать не то, что показал бы бэкенд,
 * когда проектов станет много.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useNavigationStore } from '@/stores/navigation.store'

import {
  createProject,
  listMembers,
  listProjects,
  type CreateProjectPayload,
  type Project,
  type ProjectMember,
} from './api'

export type ProjectFilter = 'all' | 'mine'

export const useProjectsStore = defineStore('projects', () => {
  const navigation = useNavigationStore()

  const items = ref<Project[]>([])
  /** Участники по id проекта — для аватаров на карточках */
  const members = ref<Record<string, ProjectMember[]>>({})
  const isLoading = ref(false)
  const error = ref<unknown>(null)
  const isLoaded = ref(false)

  const openIssueTotal = computed(() =>
    items.value.reduce((total, project) => total + project.openIssueCount, 0),
  )

  const myProjectCount = computed(
    () => items.value.filter((project) => project.myRole && project.myRole !== 'MEMBER_ROLE_INVALID').length,
  )

  /**
   * Аватары подтягиваются вторым проходом и по одному проекту: списком их не
   * отдают. Молча пропускаем отказ — приватный проект без членства виден
   * администратору, а его участники нет, и карточка без аватаров лучше, чем
   * экран с ошибкой.
   */
  const loadMembers = async (projects: Project[]) => {
    const loaded = await Promise.all(
      projects.map(async (project) => {
        try {
          return [project.id, await listMembers(project.id)] as const
        } catch {
          return [project.id, []] as const
        }
      }),
    )

    members.value = Object.fromEntries(loaded)
  }

  const load = async (params: { filter?: ProjectFilter; search?: string } = {}) => {
    isLoading.value = true
    error.value = null

    try {
      const next = await listProjects({
        onlyMine: params.filter === 'mine' || undefined,
        search: params.search,
      })

      items.value = next
      isLoaded.value = true

      // Сайдбар показывает все проекты вошедшего, а не результат фильтра
      // экрана: иначе поиск по хабу вычищал бы навигацию.
      if (!params.filter && !params.search) {
        navigation.setProjects(
          next.map((project) => ({
            key: project.key,
            name: project.name,
            color: project.color,
            openCount: project.openIssueCount,
          })),
        )
        navigation.counters = { ...navigation.counters, projects: next.length }
      }

      await loadMembers(next)
    } catch (failure) {
      error.value = failure
    } finally {
      isLoading.value = false
    }
  }

  /** Для сайдбара: список нужен на любом экране, но грузить его стоит один раз. */
  const ensureLoaded = async () => {
    if (isLoaded.value || isLoading.value) return

    await load()
  }

  const create = async (payload: CreateProjectPayload): Promise<Project> => {
    const project = await createProject(payload)

    await load()

    return project
  }

  return {
    items,
    members,
    isLoading,
    error,
    isLoaded,
    openIssueTotal,
    myProjectCount,
    load,
    ensureLoaded,
    create,
  }
})
